<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useMeetingsStore } from '@/views/Meetings/store'
import { useProjectsStore } from '@/views/Projects/store'
import { useApplicationsStore } from '@/views/Applications/store'
import { formatDate } from '@/utils/formatters'
import { getMeetingStatusLabel, getMeetingTypeLabel } from '@/types/enums'
import type { Meeting } from '@/types'

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Input } from '@/components/ui/input'
import {
  CalendarDays,
  Clock,
  MapPin,
  Users,
  ChevronRight,
  CalendarCheck,
  FolderKanban,
} from 'lucide-vue-next'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
}>()

const router = useRouter()
const meetingsStore = useMeetingsStore()
const projectsStore = useProjectsStore()
const applicationsStore = useApplicationsStore()

const pastFilter = ref<'all' | 'completed' | 'cancelled'>('all')
const pastDateFrom = ref('')
const pastDateTo = ref('')

watch(() => props.open, async (isOpen) => {
  if (isOpen && meetingsStore.meetings.length === 0) {
    await meetingsStore.fetchMeetings()
  }
  if (isOpen && projectsStore.projects.length === 0) {
    await projectsStore.fetchProjects()
  }
  if (isOpen && applicationsStore.applications.length === 0) {
    await applicationsStore.fetchApplications()
  }
})

const nextMeeting = computed(() => {
  return meetingsStore.upcomingMeetings[0] ?? null
})

const upcomingList = computed(() => {
  return meetingsStore.upcomingMeetings.slice(1, 6)
})

const pastMeetings = computed(() => {
  const now = new Date()
  let past = meetingsStore.meetings.filter((m) => {
    if (m.status === 'completed' || m.status === 'cancelled') return true
    if (m.status === 'scheduled' && new Date(m.scheduledDate) < now) return true
    return false
  })

  if (pastFilter.value !== 'all') {
    past = past.filter((m) => m.status === pastFilter.value)
  }

  if (pastDateFrom.value) {
    const from = new Date(pastDateFrom.value + 'T00:00:00')
    past = past.filter((m) => new Date(m.scheduledDate) >= from)
  }

  if (pastDateTo.value) {
    const to = new Date(pastDateTo.value + 'T23:59:59')
    past = past.filter((m) => new Date(m.scheduledDate) <= to)
  }

  return past
    .sort((a, b) => new Date(b.scheduledDate).getTime() - new Date(a.scheduledDate).getTime())
    .slice(0, 10)
})

const hasDateFilter = computed(() => !!pastDateFrom.value || !!pastDateTo.value)

const clearDateFilter = () => {
  pastDateFrom.value = ''
  pastDateTo.value = ''
}

const getProjectName = (projectId: string): string => {
  const project = projectsStore.getProjectById(projectId)
  return project?.name ?? ''
}

const getAppName = (applicationId?: string): string => {
  if (!applicationId) return ''
  const app = applicationsStore.applications.find((a) => a.id === applicationId)
  return app?.name ?? ''
}

const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'completed': return 'secondary'
    case 'cancelled': return 'destructive'
    case 'in_progress': return 'default'
    case 'postponed': return 'outline'
    default: return 'default'
  }
}

const goToMeeting = (meeting: Meeting) => {
  emit('update:open', false)
  router.push({ name: 'meeting-detail', params: { id: meeting.projectId, meetingId: meeting.id } })
}

const goToCalendar = () => {
  emit('update:open', false)
  router.push('/calendar')
}
</script>

<template>
  <Sheet :open="props.open" @update:open="emit('update:open', $event)">
    <SheetContent side="right" class="w-full sm:max-w-md p-0 flex flex-col">
      <SheetHeader class="px-6 py-4 border-b">
        <SheetTitle class="flex items-center gap-2">
          <CalendarDays class="h-5 w-5" />
          Reuniones
          <Badge v-if="meetingsStore.upcomingMeetings.length > 0" variant="default" class="ml-1">
            {{ meetingsStore.upcomingMeetings.length }}
          </Badge>
        </SheetTitle>
      </SheetHeader>

      <ScrollArea class="flex-1">
        <!-- Loading state -->
        <div v-if="meetingsStore.isLoading" class="p-6 space-y-4">
          <div v-for="i in 3" :key="i" class="space-y-2">
            <div class="h-4 bg-muted animate-pulse rounded w-3/4"></div>
            <div class="h-3 bg-muted animate-pulse rounded w-1/2"></div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else-if="meetingsStore.meetings.length === 0" class="p-6 text-center text-muted-foreground">
          <CalendarDays class="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No hay reuniones programadas</p>
        </div>

        <div v-else class="pb-2">
          <!-- Section 1: Next Meeting (featured) -->
          <div v-if="nextMeeting" class="p-4">
            <p class="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Proxima reunion
            </p>
            <div
              class="border border-primary rounded-lg bg-primary/5 p-4 cursor-pointer hover:bg-primary/10 transition-colors"
              @click="goToMeeting(nextMeeting)"
            >
              <div class="flex items-start justify-between gap-2 mb-2">
                <h3 class="text-sm font-semibold leading-tight">{{ nextMeeting.title }}</h3>
                <Badge :variant="getStatusVariant(nextMeeting.status)" class="shrink-0 text-[10px]">
                  {{ getMeetingStatusLabel(nextMeeting.status) }}
                </Badge>
              </div>
              <div class="space-y-1.5 text-xs text-muted-foreground">
                <div class="flex items-center gap-1.5">
                  <CalendarDays class="h-3.5 w-3.5" />
                  {{ formatDate(nextMeeting.scheduledDate, 'long') }}
                </div>
                <div v-if="nextMeeting.startTime" class="flex items-center gap-1.5">
                  <Clock class="h-3.5 w-3.5" />
                  {{ nextMeeting.startTime }}
                  <template v-if="nextMeeting.endTime"> - {{ nextMeeting.endTime }}</template>
                </div>
                <div v-if="nextMeeting.location" class="flex items-center gap-1.5">
                  <MapPin class="h-3.5 w-3.5" />
                  {{ nextMeeting.location }}
                </div>
                <div v-if="nextMeeting.attendees.length > 0" class="flex items-center gap-1.5">
                  <Users class="h-3.5 w-3.5" />
                  {{ nextMeeting.attendees.length }} asistentes
                </div>
                <div class="flex items-center gap-2 pt-1">
                  <Badge variant="outline" class="text-[10px]">
                    {{ getMeetingTypeLabel(nextMeeting.meetingType) }}
                  </Badge>
                  <span v-if="getProjectName(nextMeeting.projectId)" class="text-muted-foreground truncate">
                    {{ getProjectName(nextMeeting.projectId) }}
                    <template v-if="getAppName(nextMeeting.applicationId)">
                      · {{ getAppName(nextMeeting.applicationId) }}
                    </template>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 2: Upcoming Meetings -->
          <div v-if="upcomingList.length > 0" class="px-4 pb-2">
            <Separator class="mb-3" />
            <p class="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Proximas reuniones
            </p>
            <div class="space-y-1">
              <div
                v-for="meeting in upcomingList"
                :key="meeting.id"
                class="flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-accent/50 transition-colors"
                @click="goToMeeting(meeting)"
              >
                <div class="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <CalendarCheck class="h-4 w-4 text-primary" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium truncate">{{ meeting.title }}</p>
                  <p class="text-xs text-muted-foreground">
                    {{ formatDate(meeting.scheduledDate, 'short') }}
                    <template v-if="meeting.startTime"> · {{ meeting.startTime }}</template>
                  </p>
                  <p v-if="getProjectName(meeting.projectId)" class="text-xs text-muted-foreground/70 truncate flex items-center gap-1">
                    <FolderKanban class="h-3 w-3 inline shrink-0" />
                    {{ getProjectName(meeting.projectId) }}
                    <template v-if="getAppName(meeting.applicationId)">
                      · {{ getAppName(meeting.applicationId) }}
                    </template>
                  </p>
                </div>
                <ChevronRight class="h-4 w-4 text-muted-foreground shrink-0" />
              </div>
            </div>
          </div>

          <!-- Section 3: Past Meetings -->
          <div v-if="pastMeetings.length > 0 || pastFilter !== 'all' || hasDateFilter" class="px-4 pb-2">
            <Separator class="mb-3" />
            <div class="flex items-center justify-between mb-3">
              <p class="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Reuniones pasadas
              </p>
            </div>
            <div class="flex gap-1 mb-3">
              <Button
                variant="ghost"
                size="sm"
                class="h-7 text-xs px-2.5"
                :class="{ 'bg-accent': pastFilter === 'all' }"
                @click="pastFilter = 'all'"
              >
                Todas
              </Button>
              <Button
                variant="ghost"
                size="sm"
                class="h-7 text-xs px-2.5"
                :class="{ 'bg-accent': pastFilter === 'completed' }"
                @click="pastFilter = 'completed'"
              >
                Completadas
              </Button>
              <Button
                variant="ghost"
                size="sm"
                class="h-7 text-xs px-2.5"
                :class="{ 'bg-accent': pastFilter === 'cancelled' }"
                @click="pastFilter = 'cancelled'"
              >
                Canceladas
              </Button>
            </div>
            <div class="flex items-center gap-2 mb-3">
              <Input
                v-model="pastDateFrom"
                type="date"
                class="h-7 text-xs flex-1"
                placeholder="Desde"
              />
              <span class="text-xs text-muted-foreground">-</span>
              <Input
                v-model="pastDateTo"
                type="date"
                class="h-7 text-xs flex-1"
                placeholder="Hasta"
              />
              <Button
                v-if="hasDateFilter"
                variant="ghost"
                size="sm"
                class="h-7 text-xs px-2"
                @click="clearDateFilter"
              >
                Limpiar
              </Button>
            </div>
            <div class="space-y-1">
              <div
                v-for="meeting in pastMeetings"
                :key="meeting.id"
                class="flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-accent/50 transition-colors"
                @click="goToMeeting(meeting)"
              >
                <div class="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <CalendarDays class="h-4 w-4 text-muted-foreground" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm truncate">{{ meeting.title }}</p>
                  <p class="text-xs text-muted-foreground">
                    {{ formatDate(meeting.scheduledDate, 'short') }}
                  </p>
                </div>
                <Badge :variant="getStatusVariant(meeting.status)" class="shrink-0 text-[10px]">
                  {{ getMeetingStatusLabel(meeting.status) }}
                </Badge>
              </div>
            </div>
            <p v-if="pastMeetings.length === 0" class="text-xs text-muted-foreground text-center py-3">
              No hay reuniones con este filtro
            </p>
          </div>
        </div>
      </ScrollArea>

      <Separator />
      <div class="p-4">
        <Button variant="outline" class="w-full" @click="goToCalendar">
          Ver calendario completo
        </Button>
      </div>
    </SheetContent>
  </Sheet>
</template>
